import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import {
  Card,
  Row,
  Col,
  Button,
  Spinner,
  Alert,
  Badge,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
} from "../../components/Component";
import {
  Table,
  Collapse as AntCollapse,
  TableColumnsType,
  Timeline,
  Popover,
  Empty,
  Flex,
  Spin,
  Tag,
  Modal,
  Form,
  Input,
  Upload,
  message,
  Select,
  Radio,
  Divider,
  Card as AntCard,
  Space,
  Typography,
} from "antd";
import {
  PaperClipOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UploadOutlined,
  DeleteOutlined,
  InboxOutlined,
  FileTextOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { mockWorkOrders } from "../../data/mockWorkOrders";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { formatCurrency } from "../../utils/Utils";
import workOrderApis from "../../api/WorkOrderManagement/WorkOrderApis";

const { Panel } = AntCollapse;
const { TextArea } = Input;
const { Option } = Select;
const { Title, Text } = Typography;
const { Dragger } = Upload;

const WorkOrderViewDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [workOrder, setWorkOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [screenSize, setScreenSize] = useState(0);
  const [mobileView, setMobileView] = useState(false);
  const [sm, setSm] = useState(false);
  const [shareOptionsToggle, setShareOptionsToggle] = useState(false);
  const [actionModalVisible, setActionModalVisible] = useState(false);
  const [actionType, setActionType] = useState<
    "accept" | "reject" | "request_changes" | "complete" | null
  >(null);
  const [form] = Form.useForm();
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (id) {
      loadWorkOrderData(id);
    }
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, [id]);

  const viewChange = () => {
    setScreenSize(window.innerWidth);
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  const loadWorkOrderData = async (workOrderId: string) => {
    try {
      setLoading(true);
      if (!workOrderId) {
        message.error("Work Order ID is required");
        setLoading(false);
        return;
      }
      const response: any = await workOrderApis.getWorkOrderById(workOrderId);

      if (response && response.status && response.data) {
        setWorkOrder(response.data);
      } else {
        throw new Error(
          response?.message || "Failed to fetch Work Order details"
        );
      }
    } catch (error) {
      console.error("Error loading work order:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load work order data",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptConfirmation = () => {
    Swal.fire({
      title: "Accept Work Order",
      text: "Are you sure you want to accept this work order?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Accept",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setSubmitting(true);
          console.log("Accepting work order:", id);

          const response: any = await workOrderApis.confirmByContractor({
            workOrderId: id,
            action: "Accept",
          });

          if (response.data) {
            Swal.fire({
              icon: "success",
              title: "Work Order Accepted!",
              text: "Work order has been accepted successfully",
              confirmButtonText: "OK",
            }).then(() => {
              navigate(`${process.env.PUBLIC_URL}/work-order-execution`);
            });
          } else {
            throw new Error("Failed to accept work order");
          }
        } catch (error) {
          console.error("Error accepting work order:", error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to accept work order. Please try again.",
            confirmButtonText: "OK",
          });
        } finally {
          setSubmitting(false);
        }
      }
    });
  };

  const openActionModal = (type: "reject" | "request_changes" | "complete") => {
    setActionType(type);
    setActionModalVisible(true);
    form.resetFields();
    setUploadedFiles([]);
    setSubmitting(false);
  };

  const handleActionSubmit = async (values: any) => {
    try {
      setSubmitting(true);
      console.log("Submitting action:", actionType, values);

      let message = "";
      let navigationPath = "";

      switch (actionType) {
        case "reject":
          await workOrderApis.confirmByContractor({
            workOrderId: id,
            action: "Reject",
            reason: values.reason || "",
          });
          message = "Work order rejected successfully!";
          navigationPath = "/New-work-order";
          break;
        case "request_changes":
          await handleChangeRequest(values);
          message = "Change request submitted successfully!";
          navigationPath = "/work-order-contractor-approvals";
          break;
        case "complete":
          message = "Work order marked as complete successfully!";
          navigationPath = "/view-work-orders";
          break;
      }

      if (actionType !== "request_changes") {
        Swal.fire("Success!", message, "success").then(() => {
          navigate(navigationPath);
        });
      }

      setActionModalVisible(false);
      form.resetFields();
      setUploadedFiles([]);
    } catch (error) {
      console.error("Error submitting action:", error);
      Swal.fire("Error!", "Failed to submit action", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangeRequest = async (values: any) => {
    try {
      const formData = new FormData();

      // Create filesData array if files are uploaded
      const filesData = uploadedFiles.map((file, index) => ({
        order: index,
        module: "Work Order Management",
        moduleFileType: "Execution Report",
      }));

      const changeRequestData = {
        workOrderId: workOrder.workOrderId,
        changeReason: values.description,
        filesData: filesData,
      };

      formData.append("changeRequest", JSON.stringify(changeRequestData));

      if (uploadedFiles.length > 0) {
        uploadedFiles.forEach((file, index) => {
          formData.append("files", file);
        });
      }

      const response: any = await workOrderApis.contractorChangeRequest(
        formData
      );

      if (response && response.status) {
        message.success("Change request submitted successfully!");
        setTimeout(() => {
          navigate("/work-order-contractor-approvals");
        }, 1500);
      } else {
        throw new Error(response?.message || "Failed to submit change request");
      }
    } catch (error) {
      console.error("Error submitting change request:", error);
      throw error;
    }
  };

  const handleFileUpload = (file: any) => {
    setUploadedFiles((prev) => [...prev, file]);
    return false; // Prevent default upload behavior
  };

  const handleFileRemove = (fileToRemove: any) => {
    setUploadedFiles((prev) => prev.filter((file) => file !== fileToRemove));
  };

  const uploadProps = {
    accept: ".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx",
    multiple: true,
    beforeUpload: handleFileUpload,
    onRemove: handleFileRemove,
    fileList: uploadedFiles.map((file, index) => ({
      uid: index.toString(),
      name: file.name,
      status: "done" as const,
      originFileObj: file,
    })),
  };

  const getStatusColor = (status: string): string => {
    const statusColors: { [key: string]: string } = {
      Pending: "warning",
      Accepted: "success",
      Rejected: "danger",
      "Under Negotiation": "info",
      Finalized: "success",
    };
    return statusColors[status] || "secondary";
  };

  // Work Order Lines Table Columns
  const workOrderLinesColumns: TableColumnsType<any> = [
    {
      title: "Task Name",
      dataIndex: "taskTitle",
      key: "taskTitle",
      width: 200,
      fixed: "left",
      render: (text: string) => <div className="fw-bold">{text}</div>,
    },
    {
      title: "Work Category",
      dataIndex: "workCategoryName",
      key: "workCategoryName",
      width: 150,
    },
    {
      title: "Budget Details",
      key: "budgetDetails",
      width: 200,
      render: (_, record: any) => (
        <div>
          {record.budgetLineName ? (
            <>
              <div className="fw-bold text-primary">{record.budgetLineName}</div>
              <div className="text-muted small">
                Allocation: {formatCurrency(record.budgetAllocation || 0, "INR")}
              </div>
              {/* <div className="text-muted small">
                ID: {record.budgetLineId}
              </div> */}
            </>
          ) : (
            <span className="text-muted">No budget assigned</span>
          )}
        </div>
      ),
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: 100,
      align: "right",
      render: (quantity: number, record: any) =>
        `${quantity?.toLocaleString()} ${record?.uomName}`,
    },
    {
      title: "Rate",
      dataIndex: "rate",
      key: "rate",
      width: 120,
      align: "right",
      render: (rate: number) => formatCurrency(rate, "INR"),
    },
    {
      title: "Tax (%)",
      dataIndex: "taxName",
      key: "taxName",
      width: 120,
      align: "center",
      render: (taxName: string) => {
        if (!taxName) return "NA";
        // Extract percentage from taxName like "IGST - 18.00%"
        const percentMatch = taxName.match(/(\d+(?:\.\d+)?)%/);
        if (percentMatch) {
          return `${percentMatch[1]}%`;
        }
        return "NA";
      },
    },
    {
      title: "Tax Amount",
      dataIndex: "taxAmount",
      key: "taxAmount",
      width: 120,
      align: "right",
      render: (taxAmount: number) => (
        <span style={{ color: "#1890ff" }}>
          {formatCurrency(taxAmount || 0, "INR")}
        </span>
      ),
    },
    {
      title: "Subtotal",
      dataIndex: "lineValue",
      key: "lineValue",
      width: 120,
      align: "right",
      render: (lineValue: number, record: any) => (
        <strong>{formatCurrency(lineValue || record.total || 0, "INR")}</strong>
      ),
    },
    {
      title: "Total (incl. Tax)",
      key: "totalWithTax",
      width: 150,
      align: "right",
      render: (_, record: any) => {
        const subtotal = record.lineValue || record.total || 0;
        const tax = record.taxAmount || 0;
        const total = subtotal + tax;
        return (
          <strong style={{ color: "#52c41a" }}>
            {formatCurrency(total, "INR")}
          </strong>
        );
      },
    },
    {
      title: "Weightage",
      dataIndex: "weightagePct",
      key: "weightagePct",
      width: 100,
      align: "center",
      render: (weightage: number) =>
        weightage != null ? `${weightage}%` : "NA",
    },
  ];

  const getStatusMapper = (status: string) => {
    const statusMap: { [key: string]: { title: string; color: string } } = {
      Pending: { title: "Pending Review", color: "warning" },
      Accepted: { title: "Accepted", color: "success" },
      Rejected: { title: "Rejected", color: "danger" },
      "Under Negotiation": { title: "Under Negotiation", color: "info" },
      Finalized: { title: "Finalized", color: "success" },
    };
    return statusMap[status] || { title: status, color: "secondary" };
  };

  if (loading) {
    return (
      <>
        <Head title="Work Order Details" />
        <Content>
          <Flex align="center" gap="middle" className="pageLoader">
            <Spin size="large" className="m-auto" />
          </Flex>
        </Content>
      </>
    );
  }

  if (!workOrder) {
    return (
      <>
        <Head title="Work Order Not Found" />
        <Content>
          <div className="text-center py-5">
            <Empty description="Work Order not found" />
          </div>
        </Content>
      </>
    );
  }

  const statusInfo = getStatusMapper(workOrder.status);
  const isSlaBreach = workOrder.slaAge > 2 && workOrder.status === "Pending";

  return (
    <>
      <Head title={`Work Order - ${workOrder.workOrderNumber}`} />
      <Content>
        <Card className="card-bordered">
          <div className="card-aside-wrap">
            <div className="card-inner card-inner-lg">
              {sm && mobileView && (
                <div
                  className="toggle-overlay"
                  onClick={() => setSm(!sm)}
                ></div>
              )}
              <BlockHead size="lg">
                <BlockBetween>
                  <BlockHeadContent>
                    <BlockTitle tag="h4">
                      <span>
                        {workOrder.workOrderNumber} | {statusInfo.title}
                      </span>
                      {/* {isSlaBreach && (
                        <Badge color="danger" className="ml-2">SLA Breach</Badge>
                      )} */}
                    </BlockTitle>
                  </BlockHeadContent>
                  <div className="d-flex">
                    {/* Action buttons based on status */}

                    <div className="actions clearfix">
                      <ul
                        className="m-auto d-flex align-items-center justify-content-center p-0"
                        style={{ flexDirection: "row-reverse" }}
                      >
                        {(workOrder.status === "Released" ||
                          workOrder.status === "Amended") && (
                          <>
                            <li className="pt-0 pb-0">
                              <Button
                                color="success"
                                onClick={handleAcceptConfirmation}
                                disabled={submitting}
                              >
                                {/* <CheckCircleOutlined />  */}
                                Accept
                              </Button>
                            </li>

                            <li className="pt-0 pb-0">
                              <Button
                                color="danger"
                                onClick={() => openActionModal("reject")}
                              >
                                {/* <CloseCircleOutlined />  */}
                                Reject
                              </Button>
                            </li>
                          </>
                        )}
                        {(workOrder.status === "Released" ||
                          workOrder.status === "Changes Requested" || workOrder.status === "Amended") && (
                          <li className="pt-0 pb-0">
                            <Button
                              color="warning"
                              onClick={() => openActionModal("request_changes")}
                            >
                              {/* <Icon name="edit" />  */}
                              Request Changes
                            </Button>
                          </li>
                        )}
                      </ul>
                    </div>

                    {/* Actions for Accepted status (In Execution) */}
                    {/* {workOrder.status === "Accepted" && (
                      <div className="actions clearfix">
                        <ul className="m-auto d-flex align-items-center justify-content-center p-0" style={{ flexDirection: "row-reverse" }}>
                          <li className="pt-0 pb-0">
                            <Button color="success" onClick={() => openActionModal('complete')}>
                              <CheckCircleOutlined /> Mark as Complete
                            </Button>
                          </li>
                        </ul>
                      </div>
                    )} */}

                    <div className="actions clearfix">
                      <ul
                        className="m-auto d-flex align-items-center justify-content-center p-0"
                        style={{ flexDirection: "row-reverse" }}
                      >
                        {/* <li className="pt-0 pb-0">
                          <Dropdown
                            isOpen={shareOptionsToggle}
                            toggle={() => {
                              setShareOptionsToggle(!shareOptionsToggle);
                            }}
                          >
                            <DropdownToggle className="btn btn-white btn-outline-light">
                              <Icon name="share"></Icon>
                              <span>Share</span>
                            </DropdownToggle>
                            <DropdownMenu>
                              <DropdownItem onClick={() => {}}>
                                <Icon name="mail"></Icon>
                                <span>Send Mail</span>
                              </DropdownItem>
                              <DropdownItem onClick={() => {}}>
                                <Icon name="download"></Icon>
                                <span>Export PDF</span>
                              </DropdownItem>
                            </DropdownMenu>
                          </Dropdown>
                        </li> */}

                        <li className="pt-0 pb-0">
                          <Button
                            className="toggle"
                            color="primary"
                            onClick={() => {
                              navigate(-1);
                            }}
                          >
                            <Icon name="arrow-left" />
                            <span>Back</span>
                          </Button>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <BlockHeadContent className="align-self-start d-lg-none">
                    <Button
                      className={`toggle btn btn-icon btn-trigger mt-n1 ${
                        sm ? "active" : ""
                      }`}
                      onClick={() => setSm(!sm)}
                    >
                      <Icon name="menu-alt-r" />
                    </Button>
                  </BlockHeadContent>
                </BlockBetween>
              </BlockHead>

              {workOrder.status === "Rejected" &&
                workOrder.contractorActions?.length > 0 && (
                  <Alert color="danger" isOpen={true}>
                    <strong>Rejected: </strong>
                    {workOrder.contractorActions.find(
                      (action: any) => action.actionType === "reject"
                    )?.reason || "No reason provided"}
                  </Alert>
                )}

              <div className="content clearfix">
                <AntCollapse defaultActiveKey={["1", "2", "3"]}>
                  <Panel
                    header={
                      <div className="d-flex align-items-center justify-content-between">
                        <>General Information</>
                      </div>
                    }
                    key="1"
                  >
                    <Col className="justify-start w-100 flex-wrap mt-3 p-3">
                      <div className="w-30 border-end">
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">WO #</div>
                          <div className="data-value w-70">
                            {workOrder.workOrderNumber || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Site</div>
                          <div className="data-value w-70">
                            {workOrder.siteName || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Project</div>
                          <div className="data-value w-70">
                            {workOrder.projectName || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Organisation</div>
                          <div className="data-value w-70">
                            <Badge color="outline-primary">
                              {workOrder.orgName || "--"}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="w-30 border-end paddingLeft10">
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Status</div>
                          <div className="data-value w-70">
                            <Badge color={`outline-${statusInfo.color}`}>
                              {statusInfo.title}
                            </Badge>
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Contract Type</div>
                          <div className="data-value w-70">
                            <Badge color="outline-info">
                              {workOrder.contractType || "--"}
                            </Badge>
                          </div>
                        </div>

                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">SLA Age</div>
                          <div className="data-value w-70">
                            <Badge
                              color={
                                isSlaBreach
                                  ? "outline-danger"
                                  : "outline-success"
                              }
                            >
                              {workOrder.slaAge} days
                            </Badge>
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">WO Value</div>
                          <div className="data-value w-70 mt-2">
                            <Badge color="outline-success">
                              {formatCurrency(
                                workOrder.grossValue || workOrder.totalValue,
                                "INR"
                              )}
                            </Badge>
                            {workOrder.enableTax && (
                              <div className="small text-muted mt-1">
                                Base:{" "}
                                {formatCurrency(
                                  workOrder.totalValue || 0,
                                  "INR"
                                )}
                                {workOrder.taxAmount > 0 && (
                                  <span>
                                    {" "}
                                    + Tax:{" "}
                                    {formatCurrency(workOrder.taxAmount, "INR")}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="w-30 paddingLeft10">
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Title</div>
                          <div className="data-value w-70">
                            <Popover
                              content={
                                <div>
                                  <p>{workOrder.workOrderTitle}</p>
                                </div>
                              }
                              title={"Work Order Title"}
                            >
                              {workOrder.workOrderTitle?.length > 30 ? (
                                <span
                                  style={{
                                    color: "#2263b3",
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                  }}
                                >
                                  {workOrder.workOrderTitle.substring(0, 30)}...
                                </span>
                              ) : (
                                workOrder.workOrderTitle || "--"
                              )}
                            </Popover>
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Description</div>
                          <div className="data-value w-70">
                            <Popover
                              content={
                                <div>
                                  <p>
                                    {workOrder.description ||
                                      "No description provided"}
                                  </p>
                                </div>
                              }
                              title={"Description"}
                            >
                              {workOrder.description &&
                              workOrder.description.length > 30 ? (
                                <span
                                  style={{
                                    color: "#2263b3",
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                  }}
                                >
                                  {workOrder.description.substring(0, 30)}...
                                </span>
                              ) : (
                                workOrder.description || "--"
                              )}
                            </Popover>
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Released Date</div>
                          <div className="data-value w-70">
                            {dayjs(
                              workOrder.releasedAt || workOrder.dateReleased
                            ).format("DD-MMM-YYYY")}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Contract Owner</div>
                          <div className="data-value w-70">
                            {workOrder.contractorName || "--"}
                          </div>
                        </div>
                        {workOrder.budgetName && (
                          <div className="data-col align-center pt-1">
                            <div className="data-label w-30">Budget</div>
                            <div className="data-value w-70">
                              <Badge color="outline-info">
                                {workOrder.budgetName}
                              </Badge>
                              {/* {workOrder.budgetHeaderId && (
                                <div className="small text-muted mt-1">
                                  ID: {workOrder.budgetHeaderId}
                                </div>
                              )} */}
                            </div>
                          </div>
                        )}
                      </div>
                    </Col>

                    {/* Tax Information Section */}
                    {workOrder.enableTax && (
                      <Col className="justify-start w-100 flex-wrap p-3">
                        <Divider>Tax Information</Divider>
                        {workOrder.taxLevel === "Line" ? (
                          // For Line level tax, show in single row centered
                          <div className="w-100 d-flex">
                            <div className="d-flex align-items-center gap-4">
                              <div className="d-flex align-items-center gap-2">
                                <span className="data-label text-nowrap w-60">Tax Enabled:</span>
                                <Badge color="outline-success">
                                  {workOrder.enableTax ? "Yes" : "No"}
                                </Badge>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <span className="data-label text-nowrap w-55">Tax Level:</span>
                                <Badge color="outline-info">
                                  {workOrder.taxLevel}
                                </Badge>
                              </div>
                              <div className="text-muted small">
                                <em>(Applied per line)</em>
                              </div>
                            </div>
                          </div>
                        ) : (
                          // For Header/Both level tax, show full 3-column layout
                          <>
                            <div className="w-30 border-end">
                              <div className="data-col align-center pt-1">
                                <div className="data-label w-30">Tax Enabled</div>
                                <div className="data-value w-70">
                                  <Badge color="outline-success">
                                    {workOrder.enableTax ? "Yes" : "No"}
                                  </Badge>
                                </div>
                              </div>
                              <div className="data-col align-center pt-1">
                                <div className="data-label w-30">Tax Level</div>
                                <div className="data-value w-70">
                                  <Badge color="outline-info">
                                    {workOrder.taxLevel || "--"}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                            <div className="w-30 border-end paddingLeft10">
                              <div className="data-col align-center pt-1">
                                <div className="data-label w-30">TDS Rate</div>
                                <div className="data-value w-70">
                                  <Badge color="outline-warning">
                                    {workOrder.tdsRate
                                      ? `${workOrder.tdsRate}%`
                                      : "--"}
                                  </Badge>
                                </div>
                              </div>
                              <div className="data-col align-center pt-1">
                                <div className="data-label w-30">TCS Rate</div>
                                <div className="data-value w-70">
                                  <Badge color="outline-warning">
                                    {workOrder.tcsRate
                                      ? `${workOrder.tcsRate}%`
                                      : "--"}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                            <div className="w-30 paddingLeft10">
                              <div className="data-col align-center pt-1">
                                <div className="data-label w-30">TDS Tax</div>
                                <div className="data-value w-70">
                                  {workOrder.tdsTaxName || "--"}
                                </div>
                              </div>
                              <div className="data-col align-center pt-1">
                                <div className="data-label w-30">TCS Tax</div>
                                <div className="data-value w-70">
                                  {workOrder.tcsTaxName || "--"}
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </Col>
                    )}
                  </Panel>

                  <Panel
                    header={`Work Order Lines (${
                      workOrder.workOrderLines?.length || 0
                    })`}
                    key="2"
                  >
                    <div className="table-responsive poTable">
                      <div
                        style={{
                          width: screenSize - 170 + "px",
                          margin: "auto",
                        }}
                      >
                        {!workOrder.workOrderLines ||
                        workOrder.workOrderLines.length === 0 ? (
                          <div className="text-center py-5">
                            <Empty description="No work order lines found" />
                          </div>
                        ) : (
                          <Table
                            className="customTable"
                            columns={workOrderLinesColumns}
                            dataSource={workOrder.workOrderLines}
                            scroll={{ x: 900 }}
                            bordered
                            size="small"
                            pagination={false}
                            summary={() => {
                              const totalSubtotal =
                                workOrder.workOrderLines.reduce(
                                  (sum: number, line: any) =>
                                    sum + (line.lineValue || line.total || 0),
                                  0
                                );
                              const totalTaxAmount =
                                workOrder.workOrderLines.reduce(
                                  (sum: number, line: any) =>
                                    sum + (line.taxAmount || 0),
                                  0
                                );
                              const grandTotal = totalSubtotal + totalTaxAmount;

                              return (
                                <Table.Summary>
                                  <Table.Summary.Row>
                                    <Table.Summary.Cell index={0} colSpan={6}>
                                      <strong>Total</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={1}>
                                      <strong style={{ color: "#1890ff" }}>
                                        {formatCurrency(totalTaxAmount, "INR")}
                                      </strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={2}>
                                      <strong>
                                        {formatCurrency(totalSubtotal, "INR")}
                                      </strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={3}>
                                      <strong style={{ color: "#52c41a" }}>
                                        {formatCurrency(grandTotal, "INR")}
                                      </strong>
                                    </Table.Summary.Cell>
                                     <Table.Summary.Cell index={4}>
                                      {/* <strong style={{ color: "#52c41a" }}>
                                        {formatCurrency(grandTotal, "INR")}
                                      </strong> */}
                                    </Table.Summary.Cell>
                                  </Table.Summary.Row>
                                </Table.Summary>
                              );
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </Panel>

                  {/* Tax Summary Panel - Only show if tax is enabled */}
                  {workOrder.enableTax && (
                    <Panel header="Tax Summary & Financial Details" key="2.5">
                      <Row className="mt-3">
                        <Col md={8}>
                          <h6>
                            <strong>Tax Calculations:</strong>
                          </h6>
                          <div className="border rounded p-3 mb-3">
                            <div className="d-flex justify-content-between mb-2">
                              <span>Total Value (Pre-Tax):</span>
                              <strong>
                                {formatCurrency(
                                  workOrder.totalValue || 0,
                                  "INR"
                                )}
                              </strong>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                              <span>Total Tax Amount:</span>
                              <strong style={{ color: "#1890ff" }}>
                                {formatCurrency(
                                  workOrder.taxAmount || 0,
                                  "INR"
                                )}
                              </strong>
                            </div>
                            <div className="d-flex justify-content-between mb-3 pt-2 border-top">
                              <span>
                                <strong>Gross Value (Incl. Tax):</strong>
                              </span>
                              <strong style={{ color: "#52c41a" }}>
                                {formatCurrency(
                                  workOrder.grossValue || 0,
                                  "INR"
                                )}
                              </strong>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span>Outstanding Amount:</span>
                              <strong style={{ color: "#fa8c16" }}>
                                {formatCurrency(
                                  workOrder.outstandingAmount || 0,
                                  "INR"
                                )}
                              </strong>
                            </div>
                          </div>
                        </Col>
                        <Col md={4}>
                          <h6>
                            <strong>Tax Configuration:</strong>
                          </h6>
                          <div className="border rounded p-3">
                            <div className="mb-2">
                              <span className="text-muted">Tax Level:</span>
                              <div>
                                <Badge color="info">{workOrder.taxLevel}</Badge>
                              </div>
                            </div>
                            {workOrder.taxLevel === "Line" ? (
                              <div className="text-center mt-3">
                                <div className="text-muted small">
                                  <em>Individual tax rates applied per line item</em>
                                </div>
                              </div>
                            ) : (
                              <>
                                {workOrder.tdsRate && (
                                  <div className="mb-2">
                                    <span className="text-muted">TDS Rate:</span>
                                    <div>
                                      <Badge color="warning">
                                        {workOrder.tdsRate}%
                                      </Badge>
                                      {workOrder.tdsTaxName && (
                                        <div className="small text-muted mt-1">
                                          {workOrder.tdsTaxName}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                                {workOrder.tcsRate && (
                                  <div className="mb-2">
                                    <span className="text-muted">TCS Rate:</span>
                                    <div>
                                      <Badge color="warning">
                                        {workOrder.tcsRate}%
                                      </Badge>
                                      {workOrder.tcsTaxName && (
                                        <div className="small text-muted mt-1">
                                          {workOrder.tcsTaxName}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </Col>
                      </Row>
                    </Panel>
                  )}

                  <Panel
                    header="Payment Conditions & General Conditions"
                    key="3"
                  >
                    <Row className="mt-3">
                      <Col md={6}>
                        <h6>
                          <strong>Payment Conditions:</strong>
                        </h6>
                        {workOrder.workOrderPaymentConditions &&
                        workOrder.workOrderPaymentConditions.length > 0 ? (
                          <ul>
                            {workOrder.workOrderPaymentConditions.map(
                              (condition: any, index: number) => (
                                <li key={index}>
                                  <strong>{condition.conditionType}:</strong>{" "}
                                  {condition.isPercent
                                    ? `${
                                        condition.percentValue
                                      }% (${formatCurrency(
                                        condition.calculatedAmount,
                                        "INR"
                                      )})`
                                    : formatCurrency(
                                        condition.amountValue,
                                        "INR"
                                      )}
                                  {condition.interestCharged && (
                                    <span>
                                      {" "}
                                      - Interest: {condition.interestRate}%
                                    </span>
                                  )}
                                  {condition.releaseDate && (
                                    <span>
                                      {" "}
                                      - Release:{" "}
                                      {dayjs(condition.releaseDate).format(
                                        "DD-MMM-YYYY"
                                      )}
                                    </span>
                                  )}
                                  {condition.notes && (
                                    <div className="text-muted small mt-1">
                                      Notes: {condition.notes}
                                    </div>
                                  )}
                                </li>
                              )
                            )}
                          </ul>
                        ) : (
                          <div className="text-muted">
                            No payment conditions specified
                          </div>
                        )}
                      </Col>
                      <Col md={6}>
                        <h6>
                          <strong>General Conditions:</strong>
                        </h6>
                        {workOrder.workOrderGeneralConditions &&
                        workOrder.workOrderGeneralConditions.length > 0 ? (
                          <ul>
                            {workOrder.workOrderGeneralConditions.map(
                              (condition: any, index: number) => (
                                <li key={index}>
                                  <strong>{condition.conditionCode}:</strong>{" "}
                                  {condition.content}
                                  <div className="text-muted small">
                                    Updated:{" "}
                                    {dayjs(condition.updatedAt).format(
                                      "DD-MMM-YYYY"
                                    )}
                                  </div>
                                </li>
                              )
                            )}
                          </ul>
                        ) : (
                          <div className="text-muted">
                            No general conditions specified
                          </div>
                        )}
                      </Col>
                    </Row>

                    {workOrder.attachments &&
                      workOrder.attachments.length > 0 && (
                        <div className="mt-4">
                          <h6>
                            <strong>Attachments:</strong>
                          </h6>
                          <div className="d-flex flex-wrap">
                            {workOrder.attachments.map(
                              (attachment: any, index: number) => (
                                <div key={index} className="me-3 mb-2">
                                  <Badge color="outline-info">
                                    <PaperClipOutlined /> {attachment.fileName}
                                  </Badge>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}
                  </Panel>

                  {/* Change Request Details Panel - Only show if contractorChangeRequests exists */}
                  {workOrder.contractorChangeRequests &&
                    workOrder.contractorChangeRequests.length > 0 && (
                      <Panel
                        header={
                          <span>
                            Change Request Details
                            <Badge
                              color="primary"
                              style={{ marginLeft: "10px" }}
                            >
                              {workOrder.contractorChangeRequests?.length || 0}{" "}
                              Requests
                            </Badge>
                            <Badge color="info" style={{ marginLeft: "10px" }}>
                              {workOrder.contractorChangeRequests?.reduce(
                                (total: number, request: any) =>
                                  total + (request.dms?.length || 0),
                                0
                              )}{" "}
                              Files
                            </Badge>
                          </span>
                        }
                        key="5"
                      >
                        <Row>
                          <Col md={6}>
                            <h6>Change Requests</h6>
                            <Timeline>
                              {workOrder.contractorChangeRequests?.map(
                                (request: any, index: number) => (
                                  <Timeline.Item
                                    key={`request-${request.changeRequestId}`}
                                    color="blue"
                                  >
                                    <div>
                                      <div className="d-flex justify-content-between align-items-center">
                                        <strong>
                                          {" "}
                                          {dayjs(request.createdAt).format(
                                            "DD-MMM-YYYY HH:mm"
                                          )}{" "}
                                          <small>
                                            by {request.createdByName}
                                          </small>
                                        </strong>
                                        {/* <div>
                                          <Badge
                                            color="primary"
                                            style={{ marginRight: "10px" }}
                                          >
                                            Contractor
                                          </Badge>
                                        </div> */}
                                      </div>
                                      {/* <small className="text-muted">
                                      {dayjs(request.createdAt).format(
                                        "DD-MMM-YYYY HH:mm"
                                      )} by {request.createdByName}
                                    </small> */}
                                      <p className="mt-2">
                                        {request.changeReason}
                                      </p>
                                      {request.dms &&
                                        request.dms.length > 0 && (
                                          <div className="mt-2">
                                            <Badge color="info">
                                              {request.dms.length} file
                                              {request.dms.length > 1
                                                ? "s"
                                                : ""}{" "}
                                              attached
                                            </Badge>
                                          </div>
                                        )}
                                    </div>
                                  </Timeline.Item>
                                )
                              )}
                            </Timeline>
                          </Col>

                          <Col md={6}>
                            <h6>Uploaded Files</h6>
                            {workOrder.contractorChangeRequests?.some(
                              (request: any) =>
                                request.dms && request.dms.length > 0
                            ) ? (
                              <div>
                                {workOrder.contractorChangeRequests?.map(
                                  (request: any) =>
                                    request.dms?.map(
                                      (file: any, index: any) => (
                                        <div
                                          key={`${request.changeRequestId}-file-${index}`}
                                          className="card card-bordered mb-3"
                                        >
                                          <div className="card-inner">
                                            <div className="d-flex align-items-center">
                                              <div
                                                className="mr-3"
                                                style={{ fontSize: "24px" }}
                                              >
                                                <PaperClipOutlined />
                                              </div>
                                              <div className="flex-grow-1">
                                                <h6 className="mb-1">
                                                  {file.fileName ||
                                                    file.name ||
                                                    "Unknown File"}
                                                </h6>
                                                <div className="text-muted">
                                                  <small>
                                                    {file.fileSize
                                                      ? `${(
                                                          file.fileSize /
                                                          1024 /
                                                          1024
                                                        ).toFixed(2)} MB`
                                                      : "Unknown size"}{" "}
                                                    •{" "}
                                                    {file.category ||
                                                      file.moduleFileType ||
                                                      "General"}
                                                  </small>
                                                </div>
                                                <div className="text-muted">
                                                  <small>
                                                    Request #
                                                    {request.changeRequestId} •{" "}
                                                    {dayjs(
                                                      file.createdAt ||
                                                        request.createdAt
                                                    ).format("DD/MM/YYYY")}
                                                  </small>
                                                </div>
                                                {file.description && (
                                                  <div className="text-muted">
                                                    <small>
                                                      {file.description}
                                                    </small>
                                                  </div>
                                                )}
                                              </div>
                                              <div className="d-flex">
                                                <Button
                                                  size="sm"
                                                  color="primary"
                                                  outline
                                                  className="mr-1"
                                                  onClick={() => {
                                                    console.log(
                                                      "View file:",
                                                      file
                                                    );
                                                    // Implement view file functionality
                                                  }}
                                                >
                                                  <Icon name="eye" />
                                                </Button>
                                                <Button
                                                  size="sm"
                                                  color="success"
                                                  outline
                                                  onClick={() => {
                                                    console.log(
                                                      "Download file:",
                                                      file
                                                    );
                                                    // Implement download file functionality
                                                  }}
                                                >
                                                  <Icon name="download" />
                                                </Button>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      )
                                    )
                                )}
                              </div>
                            ) : (
                              <div className="text-center text-muted py-3">
                                No files uploaded
                              </div>
                            )}
                          </Col>
                        </Row>
                      </Panel>
                    )}

                  {/* Action History Panel */}
                  <Panel header="Action History" key="4">
                    <Timeline>
                      <Timeline.Item color="blue">
                        <p>
                          <strong>Work Order Released</strong>
                        </p>
                        <p className="text-muted">
                          {dayjs(workOrder.dateReleased).format("DD MMM YYYY")}{" "}
                          by {workOrder.contractOwner}
                        </p>
                      </Timeline.Item>

                      {workOrder.contractorActions?.map(
                        (action: any, index: number) => (
                          <Timeline.Item
                            key={index}
                            color={
                              action.actionType === "accept"
                                ? "green"
                                : action.actionType === "reject"
                                ? "red"
                                : "orange"
                            }
                          >
                            <p>
                              <strong>
                                {action.actionType
                                  .replace("_", " ")
                                  .toUpperCase()}
                              </strong>
                            </p>
                            <p className="text-muted">
                              {dayjs(action.timestamp).format(
                                "DD MMM YYYY HH:mm"
                              )}{" "}
                              by {action.authorizedPersonName || "Contractor"}
                            </p>
                            {action.reason && (
                              <p className="text-muted">
                                Reason: {action.reason}
                              </p>
                            )}
                            {action.comments && (
                              <p className="text-muted">
                                Comments: {action.comments}
                              </p>
                            )}
                          </Timeline.Item>
                        )
                      )}
                    </Timeline>
                  </Panel>
                </AntCollapse>
              </div>
            </div>
          </div>
        </Card>
      </Content>

      {/* Action Modal */}
      <Modal
        title={
          <div className="d-flex align-items-center">
            {actionType === "request_changes" && (
              <ExclamationCircleOutlined
                className="me-2"
                style={{ color: "#faad14" }}
              />
            )}
            {actionType === "reject" && "Reject Work Order"}
            {actionType === "request_changes" && "Submit Change Request"}
            {actionType === "complete" && "Complete Work Order"}
          </div>
        }
        open={actionModalVisible}
        onCancel={() => {
          setActionModalVisible(false);
          form.resetFields();
          setUploadedFiles([]);
        }}
        footer={null}
        width={actionType === "request_changes" ? 900 : 600}
        style={{ top: 20 }}
      >
        <Form form={form} layout="vertical" onFinish={handleActionSubmit}>
          {actionType === "reject" && (
            <Form.Item
              name="reason"
              label="Reason for Rejection"
              rules={[
                {
                  required: true,
                  message: "Please provide reason for rejection",
                },
              ]}
            >
              <TextArea
                rows={4}
                placeholder="Please explain why you are rejecting this work order..."
              />
            </Form.Item>
          )}

          {actionType === "request_changes" && (
            <div>
              <AntCard
                className="mb-4"
                style={{
                  backgroundColor: "#f6ffed",
                  border: "1px solid #b7eb8f",
                }}
              >
                <div className="d-flex align-items-center mb-2">
                  <FileTextOutlined
                    className="me-2"
                    style={{ color: "#52c41a" }}
                  />
                  <Title level={5} className="mb-0">
                    Change Request Information
                  </Title>
                </div>
                <Text type="secondary">
                  Please provide detailed information about the changes you're
                  requesting for this work order.
                </Text>
              </AntCard>

              <Form.Item
                name="description"
                label="Change Description"
                rules={[
                  {
                    required: true,
                    message: "Please describe the requested changes",
                  },
                ]}
              >
                <TextArea
                  rows={4}
                  placeholder="Provide a detailed description of the changes you are requesting..."
                  showCount
                  maxLength={1000}
                />
              </Form.Item>

              <Divider orientation="left">Supporting Documents</Divider>

              <Form.Item
                label="Upload Files"
                // help="Upload supporting documents, images, or specifications (Max 10MB per file)"
              >
                <Dragger {...uploadProps}>
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    Click or drag files to this area to upload
                  </p>
                  {/* <p className="ant-upload-hint">
                    Support for PDF, DOC, DOCX, images, and Excel files. Maximum 5 files allowed.
                  </p> */}
                </Dragger>
              </Form.Item>

              {uploadedFiles.length > 0 && (
                <AntCard size="small" title="Uploaded Files">
                  <Space
                    direction="vertical"
                    size="small"
                    style={{ width: "100%" }}
                  >
                    {uploadedFiles.map((file, index) => (
                      <div
                        key={index}
                        className="d-flex justify-content-between align-items-center"
                      >
                        <div className="d-flex align-items-center">
                          <PaperClipOutlined className="me-2" />
                          <span>{file.name}</span>
                          <Tag color="blue" className="ms-2">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </Tag>
                        </div>
                        <Button
                          size="small"
                          // type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleFileRemove(file)}
                        />
                      </div>
                    ))}
                  </Space>
                </AntCard>
              )}
            </div>
          )}

          {actionType === "complete" && (
            <>
              <Form.Item
                name="completionNotes"
                label="Completion Notes"
                rules={[
                  {
                    required: true,
                    message: "Please provide completion notes",
                  },
                ]}
              >
                <TextArea
                  rows={4}
                  placeholder="Add notes about work completion, quality checks, etc..."
                />
              </Form.Item>
              <Form.Item
                name="completionDate"
                label="Actual Completion Date"
                rules={[
                  { required: true, message: "Please select completion date" },
                ]}
              >
                <Input type="date" />
              </Form.Item>
            </>
          )}

          <Divider />

          <div className="d-flex justify-content-between align-items-center">
            <div>
              {actionType === "request_changes" && uploadedFiles.length > 0 && (
                <Text type="secondary">
                  {uploadedFiles.length} file
                  {uploadedFiles.length > 1 ? "s" : ""} ready to upload
                </Text>
              )}
            </div>
            <Space>
              <Button
                onClick={() => {
                  setActionModalVisible(false);
                  form.resetFields();
                  setUploadedFiles([]);
                }}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                // type="primary"
                htmlType="submit"
                loading={submitting}
                icon={actionType === "request_changes" && <FileTextOutlined />}
              >
                {submitting ? "Submitting..." : "Submit"}{" "}
                {actionType === "reject"
                  ? "Rejection"
                  : actionType === "request_changes"
                  ? "Change Request"
                  : "Completion"}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default WorkOrderViewDetails;
